import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Form, Row, Col, Button, Modal, Badge, InputGroup } from "react-bootstrap";
import axios from "axios";
import config from "../../config";
import { toast } from "react-toastify";
import DataTable from 'react-data-table-component';

const customStyles = {
  rows: {
    style: {
      minHeight: '72px',
    },
  },
  headCells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      backgroundColor: '#f8f9fa',
      fontWeight: 'bold',
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
    },
  },
};

export default function UnloadingTab({ purchaseOrderId, items, onUpdate }) {
  const [grns, setGrns] = useState([]);
  const [qualityEntries, setQualityEntries] = useState([]);
  const [unloadingEntries, setUnloadingEntries] = useState([]);
  const [showUnloadingModal, setShowUnloadingModal] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [packingMaterials, setPackingMaterials] = useState([]);
  const [outerPackingMaterials, setOuterPackingMaterials] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [unloadingForm, setUnloadingForm] = useState({
    grnId: "",
    productId: "",
    quality: "",
    unit: "",
    quantity: '',
    warehouseLocationId: "",
    remarks: "",
    packingMaterialId: "",
    quantityPerBag: '',
    numberOfBags: '',
    outerPackingMaterialId: "",
    outerNumberOfBags: '',
    maxQuantity: '', // Max quantity available to unload for the selected product/GRN
    availableQtyPerBag: '', // Quantity per bag suggested by packing material stuffing
  });

  const fetchData = useCallback(async () => {
    try {
      if (!purchaseOrderId || String(purchaseOrderId).trim().toLowerCase() === 'undefined') {
        console.warn('UnloadingTab: invalid purchaseOrderId, skipping fetchData', purchaseOrderId);
        return;
      }

      const [grnsRes, qualityRes, unloadingRes, warehouseRes, productsRes, packingMaterialsRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/grn/${purchaseOrderId}`),
        axios.get(`${config.API_BASE_URL}/quality-checks/${purchaseOrderId}`),
        axios.get(`${config.API_BASE_URL}/inward-unloading/${purchaseOrderId}`),
        axios.get(`${config.API_BASE_URL}/new-warehouses/storage`),
        axios.get(`${config.API_BASE_URL}/products?category=all&minimal=true&populateGroup=true&populateUnit=true`),
        axios.get(`${config.API_BASE_URL}/packing-materials`), // Fetch all packing materials
      ]);

      setGrns(grnsRes.data);
      setQualityEntries(qualityRes.data);
      setUnloadingEntries(unloadingRes.data);
      setWarehouses(warehouseRes.data);
      setAllProducts(productsRes.data);

      const allPackingMaterials = packingMaterialsRes.data;
      // setPackingMaterials(allPackingMaterials.filter(pm => !pm.isOuterPacking));
	  setPackingMaterials(allPackingMaterials);
      setOuterPackingMaterials(allPackingMaterials.filter(pm => pm.isOuterPacking));

    } catch (err) {
      console.error("Fetch data error:", err);
    }
  }, [purchaseOrderId]);

  useEffect(() => {
    if (!purchaseOrderId || String(purchaseOrderId).trim().toLowerCase() === 'undefined') {
      console.warn('UnloadingTab: invalid purchaseOrderId in useEffect, skipping fetchData', purchaseOrderId);
      return;
    }

    fetchData();
  }, [purchaseOrderId, fetchData]);

  useEffect(() => {
    // Calculate quantityPerBag if packing material and product are selected
    if (unloadingForm.packingMaterialId && unloadingForm.productId) {
      const selectedPackingMaterial = packingMaterials.find(pm => pm._id === unloadingForm.packingMaterialId);
      if (selectedPackingMaterial && selectedPackingMaterial.itemWiseStuffing) {
        const stuffingEntry = selectedPackingMaterial.itemWiseStuffing.find(
          stuff => stuff.product && stuff.product._id === unloadingForm.productId
        );
        if (stuffingEntry) {
          setUnloadingForm(prev => ({
            ...prev,
            availableQtyPerBag: stuffingEntry.quantity,
            quantityPerBag: stuffingEntry.quantity, // Auto-fill quantity per bag
          }));
        } else {
          setUnloadingForm(prev => ({ ...prev, availableQtyPerBag: '', quantityPerBag: '' }));
        }
      } else {
        setUnloadingForm(prev => ({ ...prev, availableQtyPerBag: '', quantityPerBag: '' }));
      }
    } else {
      setUnloadingForm(prev => ({ ...prev, availableQtyPerBag: '', quantityPerBag: '' }));
    }
  }, [unloadingForm.packingMaterialId, unloadingForm.productId, packingMaterials]);

  useEffect(() => {
    // Auto-calculate numberOfBags when total quantity or quantityPerBag changes
    const qpb = unloadingForm.quantityPerBag || unloadingForm.availableQtyPerBag;
    if (unloadingForm.quantity && qpb && qpb > 0) {
      const calculatedNumberOfBags = Math.ceil(unloadingForm.quantity / qpb);
      setUnloadingForm(prev => ({
        ...prev,
        numberOfBags: calculatedNumberOfBags,
      }));
    } else {
      setUnloadingForm(prev => ({
        ...prev,
        numberOfBags: '',
      }));
    }
  }, [unloadingForm.quantity, unloadingForm.quantityPerBag, unloadingForm.availableQtyPerBag]);

  const handleUnloadingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${config.API_BASE_URL}/inward-unloading`, {
        grnId: unloadingForm.grnId,
        purchaseOrderId,
        product: unloadingForm.productId,
        quality: unloadingForm.quality,
        quantity: unloadingForm.quantity,
        unit: unloadingForm.unit,
        warehouseLocationId: unloadingForm.warehouseLocationId,
        remark: unloadingForm.remarks,
        packingMaterialId: unloadingForm.packingMaterialId || undefined,
        quantityPerBag: unloadingForm.quantityPerBag || undefined,
        numberOfBags: unloadingForm.numberOfBags || undefined,
        outerPackingMaterialId: unloadingForm.outerPackingMaterialId || undefined,
        outerNumberOfBags: unloadingForm.outerNumberOfBags || undefined,
        type: 'IN'
      });
      toast.success("Unloading entry added");
      setUnloadingForm({
        grnId: "",
        productId: "",
        quality: "",
        unit: "",
        quantity: '',
        warehouseLocationId: "",
        remarks: "",
        packingMaterialId: "",
        quantityPerBag: '',
        numberOfBags: '',
        outerPackingMaterialId: "",
        outerNumberOfBags: '',
        maxQuantity: '',
        availableQtyPerBag: '',
      });
      setShowUnloadingModal(false);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to add unloading entry");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGRNBadge = (grnId) => {
    const grn = grns.find(g => g._id === grnId || g._id.toString() === grnId);
    return grn ? grn.grnId : 'Unknown GRN';
  };

  // Helper: Get all quality-checked products for selected GRN, minus fully unloaded
  const getUnloadingProducts = () => {
    const entries = qualityEntries.filter(q =>
      q.grnId === unloadingForm.grnId ||
      q.grnId?._id === unloadingForm.grnId
    );

    // Group by productId to aggregate quantities
    const productMap = new Map();

    entries.forEach(q => {
      let productId, productName, unit;

      if (q.isAccepted) {
        productId = q.productId?._id || q.productId;
        productName = q.productId?.productName || "";
        unit = q.unit;
      } else if (q.rejectionHandling === "ACCEPT" && q.acceptedProductId) {
        productId = q.acceptedProductId?._id || q.acceptedProductId;
        const prod = allProducts.find(p => p._id === productId);
        productName = prod?.productName || "";
        unit = prod?.unitId?.unitCode || q.unit || "";
      } else {
        // Rejected with HOLD or RETURN - skip
        return;
      }

      const qualityCheckedQty = q.quantity || 0;

      if (productMap.has(productId)) {
        const existing = productMap.get(productId);
        existing.totalQualityChecked += qualityCheckedQty;
      } else {
        productMap.set(productId, {
          productId,
          productName,
          unit,
          totalQualityChecked: qualityCheckedQty,
          totalUnloaded: 0,
        });
      }
    });

    // Calculate total unloaded for each product AFTER aggregating quality checks
    productMap.forEach((item, productId) => {
      const alreadyUnloaded = unloadingEntries
        .filter(u =>
          (u.grnId === unloadingForm.grnId || u.grnId?._id === unloadingForm.grnId) &&
          (u.product === productId || u.product?._id === productId || u.productId === productId || u.productId?._id === productId)
        )
        .reduce((sum, u) => sum + (u.quantity || 0), 0);

      item.totalUnloaded = alreadyUnloaded;
    });

    // Convert map to array and calculate available quantities
    return Array.from(productMap.values())
      .map(item => {
        const availableQty = item.totalQualityChecked - item.totalUnloaded;
        if (availableQty > 0) {
          return {
            productId: item.productId,
            productName: item.productName,
            unit: item.unit,
            availableQty,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const unloadingProducts = useMemo(getUnloadingProducts, [
    unloadingForm.grnId,
    qualityEntries,
    unloadingEntries,
    allProducts,
  ]);

  const handleUnloadingProductChange = (e) => {
    const productId = e.target.value;
    const prod = unloadingProducts.find(p => p.productId === productId);
    setUnloadingForm(prev => ({
      ...prev,
      productId,
      unit: prod?.unit || "",
      // Reset packing material related fields when product changes
      packingMaterialId: "",
      quantityPerBag: '',
      numberOfBags: '',
      outerPackingMaterialId: "",
      outerNumberOfBags: '',
      quantity: '', // Reset total quantity
      maxQuantity: prod?.availableQty || 0,
      availableQtyPerBag: '',
    }));
  };

  const handlePackingMaterialChange = (e) => {
    const pmId = e.target.value;
    setUnloadingForm(prev => ({
      ...prev,
      packingMaterialId: pmId,
      numberOfBags: '',
      quantity: '',
    }));
    // const selectedPackingMaterial = packingMaterials.find(pm => pm._id === unloadingForm.packingMaterialId);
    // if (selectedPackingMaterial && selectedPackingMaterial.itemWiseStuffing) {
    //   const stuffingEntry = selectedPackingMaterial.itemWiseStuffing.find(
    //     stuff => stuff.product && stuff.product._id === unloadingForm.productId
    //   );
    //   if (stuffingEntry) {
    //     setUnloadingForm(prev => ({
    //       ...prev,
    //       availableQtyPerBag: stuffingEntry.quantity,
    //       quantityPerBag: stuffingEntry.quantity, // Auto-fill quantity per bag
    //     }));
    //   } else {
    //     setUnloadingForm(prev => ({ ...prev, availableQtyPerBag: '', quantityPerBag: '' }));
    //   }
    // }
  };



  const handleOuterPackingMaterialChange = (e) => {
    const opmId = e.target.value;
    setUnloadingForm(prev => ({
      ...prev,
      outerPackingMaterialId: opmId,
      outerNumberOfBags: '', // Reset outer number of bags
    }));
  };

  const columns = [
    {
      name: 'GRN',
      selector: row => getGRNBadge(row.grnId?._id),
      cell: row => <Badge bg="info">{getGRNBadge(row.grnId?._id)}</Badge>,
      sortable: true,
    },
    {
      name: 'Product',
      selector: row => row.product?.productName,
      sortable: true,
    },
    {
      name: 'Quantity',
      selector: row => `${row.quantity} ${row.unit}`,
      sortable: true,
      right: true,
    },
    {
      name: 'Packing Material',
      selector: row => {
        const pm = packingMaterials.find(p => p._id === row.packingMaterialId);
        return pm ? pm.name : 'N/A';
      },
      sortable: true,
    },
    {
      name: 'Qty/Bag',
      selector: row => row.quantityPerBag || 'N/A',
      sortable: true,
      right: true,
    },
    {
      name: 'No. of Bags',
      selector: row => row.numberOfBags || 'N/A',
      sortable: true,
      right: true,
    },
    // {
    //   name: 'Outer Packing',
    //   selector: row => {
    //     const opm = outerPackingMaterials.find(p => p._id === row.outerPackingMaterialId);
    //     return opm ? opm.name : 'N/A';
    //   },
    //   sortable: true,
    // },
    // {
    //   name: 'Outer Bags',
    //   selector: row => row.outerNumberOfBags || 'N/A',
    //   sortable: true,
    //   right: true,
    // },
    {
      name: 'Location',
      selector: row => warehouses.find(w => w._id === row.warehouseLocationId)?.fullPath || 'N/A',
      sortable: true,
    },
    {
      name: 'Remarks',
      selector: row => row.remark,
      wrap: true,
      grow: 2,
    },
    {
      name: 'Date',
      selector: row => new Date(row.createdAt).toLocaleDateString('en-IN'),
      sortable: true,
      right: true,
    },
  ];

  return (
    <div className="p-4">
      <DataTable
        columns={columns}
        data={unloadingEntries.filter(e => e.type === 'IN')}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        customStyles={customStyles}
        striped
        highlightOnHover
        responsive
        subHeader
        subHeaderComponent={
          <div className="d-flex justify-content-between align-items-center w-100 mb-3">
            <div className="d-flex gap-2 align-items-center">
              <input
                type="text"
                placeholder="Search..."
                className="form-control form-control-sm"
                style={{ width: '200px' }}
                onChange={e => {
                  // Add search functionality here
                }}
              />
            </div>
            <Button variant="success" onClick={() => setShowUnloadingModal(true)}>
              <i className="fas fa-plus me-2"></i>Add Unloading
            </Button>
          </div>
        }
      />

      {/* Unloading Modal */}
      <Modal show={showUnloadingModal} onHide={() => setShowUnloadingModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Unloading Entry</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUnloadingSubmit}>
          <Modal.Body>
            <Row className="gy-3">
              <Col md={6}>
                <Form.Label>GRN *</Form.Label>
                <Form.Select
                  value={unloadingForm.grnId}
                  onChange={(e) => setUnloadingForm(prev => ({ ...prev, grnId: e.target.value }))}
                  required
                >
                  <option value="">Select GRN</option>
                  {grns.map(grn => (
                    <option key={grn._id} value={grn._id}>{grn.grnId}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Product *</Form.Label>
                <Form.Select
                  value={unloadingForm.productId}
                  onChange={handleUnloadingProductChange}
                  required
                >
                  <option value="">Select Product</option>
                  {unloadingProducts.map(prod => (
                    <option key={prod.productId} value={prod.productId}>
                      {prod.productName} (Available: {prod.availableQty} {prod.unit})
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {/* Packing Material Selection */}
              {unloadingForm.productId && (
                <>
                  <Col md={6}>
                    <Form.Label>Packing Material</Form.Label>
                    <Form.Select
                      value={unloadingForm.packingMaterialId}
                      onChange={handlePackingMaterialChange}
                    >
                      <option value="">Select Packing Material</option>
                      {packingMaterials.map(pm => (
                        <option key={pm._id} value={pm._id}>{pm.name}</option>
                      ))}
                    </Form.Select>
                  </Col>

                  {unloadingForm.packingMaterialId && (
                    <>
                      <Col md={6}>
                        <Form.Label>Quantity Per Bag</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            value={unloadingForm.quantityPerBag} 
                            placeholder={unloadingForm.availableQtyPerBag || "Enter Qty/Bag"}
                            min={1}
                            onChange={e => setUnloadingForm(prev => ({ ...prev, quantityPerBag: e.target.value === '' ? '' : Number(e.target.value) }))}
                            aria-label="Quantity per bag"
                            required={!!unloadingForm.packingMaterialId} // Make it required
                          />
                          {unloadingForm.unit && (
                            <InputGroup.Text>{unloadingForm.unit}</InputGroup.Text>
                          )}
                        </InputGroup>
                        {unloadingForm.availableQtyPerBag && unloadingForm.quantityPerBag !== unloadingForm.availableQtyPerBag && (
                          <Form.Text className="text-muted">
                            (Suggested: {unloadingForm.availableQtyPerBag} {unloadingForm.unit})
                          </Form.Text>
                        )}
                      </Col>
                      <Col md={6}>
                        <Form.Label>Number of Bags</Form.Label>
                        <Form.Control
                          type="number"
                          value={unloadingForm.numberOfBags}
                          readOnly // Make it read-only
                          plaintext // Make it look like plain text
                          required={!!unloadingForm.packingMaterialId}
                        />
                        {/* {unloadingForm.packingMaterialId && (
                          <Form.Text className="text-muted">
                            Automatically calculated based on total quantity and quantity per bag.
                          </Form.Text>
                        )} */}
                      </Col>
                    </>
                  )}
                </>
              )}

              <Col md={6}>
                <Form.Label>Total Quantity *</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    value={unloadingForm.quantity}
                    min={1}
                    max={unloadingForm.maxQuantity || ""}
                    onChange={e => {
                      let val = Number(e.target.value);
                      if (val > (unloadingForm.maxQuantity || 0)) val = unloadingForm.maxQuantity;
                      setUnloadingForm(prev => ({ ...prev, quantity: val }));
                    }}
                    required
                  />
                  {unloadingForm.unit && (
                    <InputGroup.Text>
                      {unloadingForm.unit}
                    </InputGroup.Text>
                  )}
                </InputGroup>
                {/* Display available quantity for reference */}
                {unloadingForm.productId && unloadingForm.maxQuantity && (
                  <Form.Text className="text-muted">
                    Available to unload: {unloadingForm.maxQuantity} {unloadingForm.unit}
                  </Form.Text>
                )}
              </Col>

              {/* Outer Packing Material Selection (Optional) */}
              <Col md={6} className="d-none">
                <Form.Label>Outer Packing Material (Optional)</Form.Label>
                <Form.Select
                  value={unloadingForm.outerPackingMaterialId}
                  onChange={handleOuterPackingMaterialChange}
                >
                  <option value="">Select Outer Packing Material</option>
                  {outerPackingMaterials.map(opm => (
                    <option key={opm._id} value={opm._id}>{opm.name} {opm.outerPackingCapacity ? `(Capacity: ${opm.outerPackingCapacity})` : ''}</option>
                  ))}
                </Form.Select>
              </Col>
              {unloadingForm.outerPackingMaterialId && (
                <Col md={6}>
                  <Form.Label>Number of Outer Bags</Form.Label>
                  <Form.Control
                    type="number"
                    value={unloadingForm.outerNumberOfBags}
                    min={1}
                    onChange={e => setUnloadingForm(prev => ({ ...prev, outerNumberOfBags: Number(e.target.value) }))}
                    required={!!unloadingForm.outerPackingMaterialId}
                  />
                </Col>
              )}

              <Col md={6}>
                <Form.Label>Warehouse Location</Form.Label>
                <Form.Select
                  value={unloadingForm.warehouseLocationId}
                  onChange={(e) => setUnloadingForm(prev => ({ ...prev, warehouseLocationId: e.target.value }))}
                >
                  <option value="">Select Location</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse._id} value={warehouse._id}>
                      {warehouse.fullPath}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={12}>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={unloadingForm.remarks}
                  onChange={(e) => setUnloadingForm(prev => ({ ...prev, remarks: e.target.value }))}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUnloadingModal(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding Entry...' : 'Add Entry'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
